import axios from 'axios';

import { createLogger } from '../utils/debug';

/**
 * OfflineApi service
 * 
 * This service extends the regular API service with offline support.
 * It caches successful API responses in localStorage and provides
 * fallback data when offline.
 */

// Create a logger for this module
const log = createLogger('OfflineApi');

// Create axios instance with default config and timeout
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add a request interceptor to include the auth token
api.interceptors.request.use(
  (config) => {
    log('Request:', config.method, config.url);
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add a timestamp to prevent caching by the browser
    if (config.method === 'get') {
      config.params = config.params || {};
      config.params._t = Date.now();
    }
    
    return config;
  },
  (error) => {
    log('Request error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle auth errors and cache responses
api.interceptors.response.use(
  (response) => {
    log('Response:', response.config.method, response.config.url, response.status);
    
    // Cache successful GET responses
    if (response.config.method === 'get') {
      try {
        const cacheKey = `api-cache:${response.config.url}`;
        const cacheData = {
          data: response.data,
          timestamp: Date.now(),
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        log('Cached response for:', response.config.url);
      } catch (error) {
        console.error('[OfflineApi] Error caching API response:', error);
      }
    }
    return response;
  },
  (error) => {
    log('Response error:', error.message);
    
    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      log('Unauthorized, redirecting to login');
      // Redirect to login page if unauthorized
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    
    // Handle network errors for GET requests by using cached data
    if ((!error.response && error.request) || error.code === 'ECONNABORTED') {
      log('Network error or timeout');
      
      if (error.config && error.config.method === 'get') {
        try {
          const cacheKey = `api-cache:${error.config.url}`;
          const cachedData = localStorage.getItem(cacheKey);
          
          if (cachedData) {
            const { data, timestamp } = JSON.parse(cachedData);
            
            // Add a flag to indicate this is cached data
            data._fromCache = true;
            data._cacheTimestamp = timestamp;
            
            log(`Using cached data for ${error.config.url}`);
            
            // Dispatch an event to notify the app that we're using cached data
            window.dispatchEvent(new CustomEvent('offline-data-used', {
              detail: { url: error.config.url }
            }));
            
            return Promise.resolve({
              data,
              status: 200,
              statusText: 'OK (Cached)',
              headers: {},
              config: error.config,
              _cached: true
            });
          }
        } catch (cacheError) {
          console.error('[OfflineApi] Error retrieving cached data:', cacheError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Get data from API with offline support
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} - Promise with response
 */
const get = async (url, config = {}) => {
  log('GET request:', url);
  
  try {
    // Set a reasonable timeout
    const requestConfig = {
      ...config,
      timeout: config.timeout || 30000 // 30 seconds default timeout
    };
    
    return await api.get(url, requestConfig);
  } catch (error) {
    log('GET error:', url, error.message);
    
    // If we already tried to get from cache in the interceptor and failed,
    // or if it's not a network error, just throw the error
    if (error.response || (!error.request && error.code !== 'ECONNABORTED')) {
      throw error;
    }
    
    // For network errors, check if we have offline data
    log(`Network error for ${url}, checking for offline data`);
    
    // Try to get from cache
    const cacheKey = `api-cache:${url}`;
    const cachedData = localStorage.getItem(cacheKey);
    
    if (cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        
        // Add a flag to indicate this is cached data
        data._fromCache = true;
        data._cacheTimestamp = timestamp;
        
        log(`Using cached data for ${url}`);
        
        // Dispatch an event to notify the app that we're using cached data
        window.dispatchEvent(new CustomEvent('offline-data-used', {
          detail: { url }
        }));
        
        return {
          data,
          status: 200,
          statusText: 'OK (Cached)',
          headers: {},
          config,
          _cached: true
        };
      } catch (parseError) {
        console.error('[OfflineApi] Error parsing cached data:', parseError);
        localStorage.removeItem(cacheKey); // Remove invalid cache entry
      }
    }
    
    // If no cached data, throw a custom error
    throw {
      ...error,
      isOffline: true,
      message: 'You are offline and no cached data is available'
    };
  }
};

/**
 * Post data to API with offline support
 * If offline, store the request in a queue to be sent when back online
 * @param {string} url - API endpoint
 * @param {Object} data - Data to send
 * @param {Object} config - Axios config
 * @returns {Promise} - Promise with response
 */
const post = async (url, data, config = {}) => {
  log('POST request:', url);
  
  try {
    // Set a reasonable timeout
    const requestConfig = {
      ...config,
      timeout: config.timeout || 30000 // 30 seconds default timeout
    };
    
    return await api.post(url, data, requestConfig);
  } catch (error) {
    log('POST error:', url, error.message);
    
    // If it's not a network error, just throw the error
    if (error.response || (!error.request && error.code !== 'ECONNABORTED')) {
      throw error;
    }
    
    // For network errors, queue the request for later
    log(`Network error for POST ${url}, queueing for later`);
    
    // Get the current queue or initialize a new one
    const queueKey = 'api-queue';
    let queue = [];
    
    try {
      const queueData = localStorage.getItem(queueKey);
      if (queueData) {
        queue = JSON.parse(queueData);
      }
    } catch (queueError) {
      console.error('[OfflineApi] Error retrieving queue:', queueError);
    }
    
    // Generate a unique ID for this request
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Add the request to the queue
    queue.push({
      id: requestId,
      method: 'post',
      url,
      data,
      timestamp: Date.now()
    });
    
    // Save the updated queue
    localStorage.setItem(queueKey, JSON.stringify(queue));
    
    // Register for sync if service worker is available
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-api-queue');
        log('Sync registered for api-queue');
      } catch (syncError) {
        console.error('[OfflineApi] Error registering for sync:', syncError);
      }
    }
    
    // Dispatch an event to notify the app that we've queued a request
    window.dispatchEvent(new CustomEvent('request-queued', {
      detail: { url, method: 'post', id: requestId }
    }));
    
    // Throw a custom error
    throw {
      ...error,
      isOffline: true,
      isQueued: true,
      requestId,
      message: 'You are offline, but your request has been queued for later'
    };
  }
};

/**
 * Put data to API with offline support
 * If offline, store the request in a queue to be sent when back online
 * @param {string} url - API endpoint
 * @param {Object} data - Data to send
 * @param {Object} config - Axios config
 * @returns {Promise} - Promise with response
 */
const put = async (url, data, config = {}) => {
  log('PUT request:', url);
  
  try {
    // Set a reasonable timeout
    const requestConfig = {
      ...config,
      timeout: config.timeout || 30000 // 30 seconds default timeout
    };
    
    return await api.put(url, data, requestConfig);
  } catch (error) {
    log('PUT error:', url, error.message);
    
    // If it's not a network error, just throw the error
    if (error.response || (!error.request && error.code !== 'ECONNABORTED')) {
      throw error;
    }
    
    // For network errors, queue the request for later
    log(`Network error for PUT ${url}, queueing for later`);
    
    // Get the current queue or initialize a new one
    const queueKey = 'api-queue';
    let queue = [];
    
    try {
      const queueData = localStorage.getItem(queueKey);
      if (queueData) {
        queue = JSON.parse(queueData);
      }
    } catch (queueError) {
      console.error('[OfflineApi] Error retrieving queue:', queueError);
    }
    
    // Generate a unique ID for this request
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Add the request to the queue
    queue.push({
      id: requestId,
      method: 'put',
      url,
      data,
      timestamp: Date.now()
    });
    
    // Save the updated queue
    localStorage.setItem(queueKey, JSON.stringify(queue));
    
    // Register for sync if service worker is available
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-api-queue');
        log('Sync registered for api-queue');
      } catch (syncError) {
        console.error('[OfflineApi] Error registering for sync:', syncError);
      }
    }
    
    // Dispatch an event to notify the app that we've queued a request
    window.dispatchEvent(new CustomEvent('request-queued', {
      detail: { url, method: 'put', id: requestId }
    }));
    
    // Throw a custom error
    throw {
      ...error,
      isOffline: true,
      isQueued: true,
      requestId,
      message: 'You are offline, but your request has been queued for later'
    };
  }
};

/**
 * Delete data from API with offline support
 * If offline, store the request in a queue to be sent when back online
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} - Promise with response
 */
const del = async (url, config = {}) => {
  log('DELETE request:', url);
  
  try {
    // Set a reasonable timeout
    const requestConfig = {
      ...config,
      timeout: config.timeout || 30000 // 30 seconds default timeout
    };
    
    return await api.delete(url, requestConfig);
  } catch (error) {
    log('DELETE error:', url, error.message);
    
    // If it's not a network error, just throw the error
    if (error.response || (!error.request && error.code !== 'ECONNABORTED')) {
      throw error;
    }
    
    // For network errors, queue the request for later
    log(`Network error for DELETE ${url}, queueing for later`);
    
    // Get the current queue or initialize a new one
    const queueKey = 'api-queue';
    let queue = [];
    
    try {
      const queueData = localStorage.getItem(queueKey);
      if (queueData) {
        queue = JSON.parse(queueData);
      }
    } catch (queueError) {
      console.error('[OfflineApi] Error retrieving queue:', queueError);
    }
    
    // Generate a unique ID for this request
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Add the request to the queue
    queue.push({
      id: requestId,
      method: 'delete',
      url,
      timestamp: Date.now()
    });
    
    // Save the updated queue
    localStorage.setItem(queueKey, JSON.stringify(queue));
    
    // Register for sync if service worker is available
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-api-queue');
        log('Sync registered for api-queue');
      } catch (syncError) {
        console.error('[OfflineApi] Error registering for sync:', syncError);
      }
    }
    
    // Dispatch an event to notify the app that we've queued a request
    window.dispatchEvent(new CustomEvent('request-queued', {
      detail: { url, method: 'delete', id: requestId }
    }));
    
    // Throw a custom error
    throw {
      ...error,
      isOffline: true,
      isQueued: true,
      requestId,
      message: 'You are offline, but your request has been queued for later'
    };
  }
};

/**
 * Process the API request queue
 * This should be called when the app comes back online
 * @returns {Promise} - Promise that resolves when all queued requests are processed
 */
const processQueue = async () => {
  const queueKey = 'api-queue';
  let queue = [];
  
  try {
    const queueData = localStorage.getItem(queueKey);
    if (queueData) {
      queue = JSON.parse(queueData);
    }
  } catch (queueError) {
    console.error('[OfflineApi] Error retrieving queue:', queueError);
    return;
  }
  
  if (queue.length === 0) {
    log('No queued requests to process');
    return;
  }
  
  log(`Processing ${queue.length} queued requests`);
  
  // Process each request in the queue
  const results = [];
  const failedRequests = [];
  
  for (const request of queue) {
    try {
      log(`Processing queued ${request.method} request to ${request.url}`);
      
      let response;
      
      switch (request.method) {
        case 'post':
          response = await api.post(request.url, request.data);
          break;
        case 'put':
          response = await api.put(request.url, request.data);
          break;
        case 'delete':
          response = await api.delete(request.url);
          break;
        default:
          console.error(`[OfflineApi] Unknown method ${request.method}`);
          failedRequests.push(request);
          continue;
      }
      
      results.push({
        success: true,
        request,
        response
      });
      
      // Dispatch an event to notify the app that a queued request was processed
      window.dispatchEvent(new CustomEvent('queued-request-processed', {
        detail: { 
          success: true, 
          request, 
          response 
        }
      }));
      
      log(`Successfully processed queued ${request.method} request to ${request.url}`);
    } catch (error) {
      log(`Failed to process queued ${request.method} request to ${request.url}:`, error.message);
      
      results.push({
        success: false,
        request,
        error
      });
      
      // Dispatch an event to notify the app that a queued request failed
      window.dispatchEvent(new CustomEvent('queued-request-processed', {
        detail: { 
          success: false, 
          request, 
          error 
        }
      }));
      
      // If it's a network error, keep the request in the queue
      if (!error.response && (error.request || error.code === 'ECONNABORTED')) {
        failedRequests.push(request);
      }
      
      // If it's an auth error, stop processing
      if (error.response && error.response.status === 401) {
        break;
      }
    }
  }
  
  // Update the queue with only the failed requests
  if (failedRequests.length > 0) {
    localStorage.setItem(queueKey, JSON.stringify(failedRequests));
    log(`${failedRequests.length} requests remain in queue due to errors`);
  } else {
    // Clear the queue if all requests were processed
    localStorage.removeItem(queueKey);
    log('All queued requests processed successfully');
  }
  
  return results;
};

// Listen for online events to process the queue
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    log('App is back online, processing queued requests');
    
    // Notify the app that we're back online
    window.dispatchEvent(new CustomEvent('app-online'));
    
    // Wait a moment to ensure the connection is stable
    setTimeout(() => {
      processQueue()
        .then(results => {
          if (results && results.length > 0) {
            log(`Processed ${results.length} queued requests`);
            
            // Dispatch a custom event that components can listen for
            window.dispatchEvent(new CustomEvent('api-queue-processed', {
              detail: { results }
            }));
          }
        })
        .catch(error => {
          console.error('[OfflineApi] Error processing queue:', error);
          
          // Dispatch an event to notify the app that queue processing failed
          window.dispatchEvent(new CustomEvent('api-queue-error', {
            detail: { error }
          }));
        });
    }, 1000); // Wait 1 second before processing the queue
  });
  
  // Also listen for offline events
  window.addEventListener('offline', () => {
    log('App is offline');
    
    // Notify the app that we're offline
    window.dispatchEvent(new CustomEvent('app-offline'));
  });
}

// Export the API methods
export default {
  get,
  post,
  put,
  delete: del,
  processQueue,
  axios: api
};
